<template>
  <div class="page-wrapper">
    <BaseContainer :backgroundColor="backgroundColor" :colMd="6">
      <Loader
        v-show="isLoading"
        class="overlay"
        :text="loadingText"
        :textColor="foregroundColor"
        :spinnerColor="foregroundColor"
      />
      <v-container v-show="!isLoading">
        <Header
          :imageUrl="imageUrl"
          :title="title"
          :subtitle="subtitle"
          :foregroundColor="foregroundColor"
          imageSize="large"
        />
          <EmailPasswordEntry
            @isLoading="isLoadingStateChange"
            @successfulSubmission="successfulSubmission"
            :businessId="businessId"
            :businessName="businessName"
            :foregroundColor="foregroundColor"
            :backgroundColor="backgroundColor"
          />
      </v-container>
      <OverlayDialog
        v-model="showDialog"
        :title="dialogTitle"
        :message="dialogMessage"
        :foregroundColor="foregroundColor"
        :backgroundColor="backgroundColor"
        @dismissed="onDialogClosed"
      />
    </BaseContainer>
  </div>
</template>

<script>
import BaseContainer from "@/components/Atoms/BaseContainer.vue";
import Loader from "@/components/Molecules/BrandedLoader.vue";
import Header from "@/components/Molecules/BrandedHeader.vue";
import EmailPasswordEntry from "@/components/Organisms/FormEmailPasswordEntry.vue";
import OverlayDialog from "@/components/Molecules/OverlayDialog.vue";
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  isBusinessActive,
  getBusinessLogoImageUrl,
  getBusinessDisplayData,
} from "../../setupHandler.js";
import { track } from "../../analytics.js";
import { warmPassFunction } from "../../firebase-functions.js";
import { getBusinessDisplayName } from "../../firebase-business.js";

export default {
  components: {
    BaseContainer,
    Loader,
    Header,
    EmailPasswordEntry,
    OverlayDialog,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const businessId = route.params.businessId;
    const loadingText = ref("Loading, please wait a sec 😎");
    const showDialog = ref(false);
    const dialogTitle = ref("Could not find business ⚠️");
    const dialogMessage = ref(
      "The business you are looking for does not have an active rewards scheme with us, at least not yet 😊"
    );
    const isLoading = ref(true);
    const isActive = ref(false);
    const imageUrl = ref("");
    const title = ref("");
    const subtitle = ref("");
    const businessName = ref("");
    const foregroundColor = ref("rgb(0, 109, 62)");
    const backgroundColor = ref("rgb(255,255,255)");
    const dialogClosedCallback = ref(null);

    const isLoadingStateChange = (isLoadingState) => {
      loadingText.value =
        isLoadingState === true
          ? "Generating your rewards card, please hold on this may take a few seconds 😎"
          : "Loading, please wait a sec 😎";
      isLoading.value = isLoadingState;
    };

    // In-app webviews (Instagram, TikTok, etc.) cannot add wallet passes, so we
    // must not fire a delivery there - the success screen shows an "open in
    // Safari/Chrome" hint instead.
    const isInAppWebview = () =>
      /Instagram|FBAN|FBAV|FB_IAB|TikTok|musical_ly|Line\//i.test(
        navigator.userAgent || ""
      );

    const successfulSubmission = (deliveryInfo = {}) => {
      const { passType, passUrl, passGenError, businessId: passBusinessId } =
        deliveryInfo;

      // iOS: trigger the Add to Apple Wallet sheet the instant registration
      // completes. Safari overlays the sheet without leaving the page, so we
      // fire it here and still route to the success screen behind it. Android
      // is intentionally left to the success screen: navigating to the
      // pay.google.com save URL is a full navigation that would yank the user
      // off before they ever see a confirmation.
      const deliveryTriggered =
        passType === "ios" && !!passUrl && !passGenError && !isInAppWebview();
      if (deliveryTriggered) {
        window.location.href = passUrl;
      }

      router.push({
        path: "/completed",
        state: {
          passType: passType ?? null,
          passUrl: passUrl ?? null,
          passGenError: passGenError ?? null,
          businessId: passBusinessId ?? businessId,
          // Tells the success screen we already kicked off delivery so it does
          // not fire the Add to Wallet sheet a second time on mount.
          deliveryTriggered,
          // Authoritative brand colors already loaded for this page.
          brandColors: {
            foregroundColor: foregroundColor.value,
            backgroundColor: backgroundColor.value,
          },
        },
      });
    };

    const renderBasedOnBusinessStatus = async () => {
      isLoading.value = true;
      try {
        const active = await isBusinessActive(businessId);
        isActive.value = active;

        if (active) {
          track("enrollment_page_viewed", { business_id: businessId });
          // Warm the pass function while the customer types (fire-and-forget).
          warmPassFunction(businessId);
          // The display name only feeds the marketing consent label, so it
          // loads alongside the display data rather than as a further round
          // trip, and a miss falls back to the generic wording instead of
          // holding up the page.
          const [displayData, displayName] = await Promise.all([
            getBusinessDisplayData(businessId),
            getBusinessDisplayName(businessId).catch(() => ""),
          ]);
          title.value = displayData.title;
          subtitle.value = displayData.subtitle;
          businessName.value = displayName || "";
          foregroundColor.value = displayData.textColor;
          backgroundColor.value = displayData.backgroundColor;

          const logoUrl = await getBusinessLogoImageUrl(businessId);
          imageUrl.value = logoUrl;
          isLoading.value = false;
        } else {
          track("business_lookup_failed", { business_id: businessId, reason: "inactive" });
          await presentDialog();
          isLoading.value = false;
          router.push("/business");
        }
      } catch (error) {
        track("business_lookup_failed", { business_id: businessId, error_message: error.message });
        isLoading.value = false;
        dialogTitle.value = "Could not load business ⚠️";
        dialogMessage.value = "Something went wrong loading this page. Please try again.";
        await presentDialog();
        router.push("/business");
      }
    };

    const presentDialog = () => {
      showDialog.value = true;
      return new Promise((resolve) => {
        dialogClosedCallback.value = resolve;
      });
    };

    const onDialogClosed = () => {
      if (dialogClosedCallback.value) {
        dialogClosedCallback.value(true);
        dialogClosedCallback.value = null;
      }
    };

    onMounted(() => {
      renderBasedOnBusinessStatus();
    });

    return {
      loadingText,
      businessId,
      showDialog,
      dialogTitle,
      dialogMessage,
      successfulSubmission,
      router,
      isLoading,
      isActive,
      imageUrl,
      title,
      subtitle,
      businessName,
      foregroundColor,
      backgroundColor,
      isLoadingStateChange,
      onDialogClosed,
    };
  },
};
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(160deg, #f8fafb 0%, #eef4f0 100%);
  padding: 0;
}

.page-wrapper :deep(.v-card.pa-6) {
  padding: 17px !important;
}
</style>
